import { useAuth } from '../contexts/AuthContext';

const isFieldPermDebugEnabled = () =>
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('DEBUG_FIELD_PERMS') === '1';

const dlog = (...args) => {
    if (isFieldPermDebugEnabled()) console.log(...args);
};

export const useFieldPermissions = (moduleName) => {
    const { user } = useAuth();
    // Try to get permissions from context, then localStorage
    const permissions = user?.role?.permissions || JSON.parse(localStorage.getItem('userData'))?.role?.permissions || {};
    
    if (permissions && Object.keys(permissions).length > 0) {
        dlog(`🔍 Field Permissions Debug for ${user?.role?.name || 'Unknown Role'}:`, {
            moduleName,
            modulePermissions: permissions[moduleName],
            allPermissions: permissions,
        });
    }

    const getModulePermissions = () => {
        if (!permissions || !permissions[moduleName]) {
            dlog(`❌ No permissions found for module: ${moduleName}`);
            return { view: false, add: false, change: false, delete: false };
        }
        return permissions[moduleName];
    };

    const getFieldPermissions = () => {
        const modulePerms = getModulePermissions();
        if (!modulePerms || !modulePerms.fields) {
            dlog(`⚠️ No field permissions found for module: ${moduleName}, using wildcard`);
            return { '*': { view: modulePerms.view || false, edit: modulePerms.change || false } };
        }
        return modulePerms.fields;
    };

    const canViewField = (fieldName) => {
        const modulePerms = getModulePermissions();
        const fieldPerms = getFieldPermissions();

        // First check if module has view permission
        if (!modulePerms.view) {
            dlog(`❌ Module ${moduleName} view permission denied for field: ${fieldName}`);
            return false;
        }

        // Then check field-specific permissions
        if (fieldPerms[fieldName]) {
            const canView = fieldPerms[fieldName].view;
            dlog(`🔍 Field ${fieldName} specific view permission: ${canView}`);
            return canView;
        }

        // If no specific field permission, check wildcard
        if (fieldPerms['*']) {
            const canView = fieldPerms['*'].view;
            dlog(`🔍 Field ${fieldName} wildcard view permission: ${canView}`);
            return canView;
        }

        // If no field permissions defined, fall back to module view permission
        dlog(`🔍 Field ${fieldName} using module view permission: ${modulePerms.view}`);
        return modulePerms.view;
    };

    const canEditField = (fieldName) => {
        const modulePerms = getModulePermissions();
        const fieldPerms = getFieldPermissions();

        dlog(`🔍 canEditField Debug for ${fieldName}:`, {
            modulePerms,
            fieldPerms,
            fieldSpecific: fieldPerms[fieldName],
            wildcard: fieldPerms['*'],
        });

        // First check if module has change permission
        if (!modulePerms.change) {
            dlog(`❌ Module change permission denied for ${fieldName}`);
            return false;
        }

        // Then check field-specific permissions
        if (fieldPerms[fieldName]) {
            // If field explicitly has edit permission, use it
            if (fieldPerms[fieldName].edit === true) {
                dlog(`✅ Field ${fieldName} explicitly editable`);
                return true;
            }
            // If field explicitly has edit permission set to false, respect it
            if (fieldPerms[fieldName].edit === false) {
                dlog(`❌ Field ${fieldName} explicitly not editable`);
                return false;
            }
        }

        // If no specific field permission, check wildcard
        if (fieldPerms['*']) {
            // If wildcard edit is explicitly true, allow
            if (fieldPerms['*'].edit === true) {
                dlog(`✅ Wildcard edit permission for ${fieldName}`);
                return true;
            }
            // If wildcard edit is false BUT module change is true,
            // fall back to module change (wildcard false is likely default, not explicit)
            if (fieldPerms['*'].edit === false && modulePerms.change === true) {
                dlog(`🔍 Wildcard edit=false but module change=true for ${fieldName}, allowing edit`);
                return true;
            }
            dlog(`❌ Wildcard permission denies edit for ${fieldName}`);
            return false;
        }

        // If no field permissions defined, default to module change permission
        dlog(`🔍 No field permission defined for ${fieldName}, using module change permission: ${modulePerms.change}`);
        return modulePerms.change;
    };

    // Add a helper function to check if module is visible in UI
    const isModuleVisible = () => {
        const modulePerms = getModulePermissions();
        return modulePerms.view === true;
    };

    return {
        permissions,
        getModulePermissions,
        getFieldPermissions,
        canViewField,
        canEditField,
        isModuleVisible
    };
}; 